
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useRefundForm } from "@/hooks/use-refund-form";
import { useLocationHandling, UserLocation } from "@/hooks/use-location-handling";
import { useRefundSubmission } from "@/hooks/useRefundSubmission";
import BasicInfoStep from "./BasicInfoStep";
import AdditionalDetailsStep from "./AdditionalDetailsStep";
import FormActions from "./FormActions";
import { useAuth } from "@/contexts/auth";
import { useRequestLimitCheck } from "@/hooks/useRequestLimitCheck";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, Crown, Infinity } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface RefundFormContentProps {
  onCompletedCallback?: () => void;
  onSubmitSuccess?: (submittedRequest: any) => void;
  isPreview?: boolean;
}

const RefundFormContent = ({ 
  onCompletedCallback, 
  onSubmitSuccess, 
  isPreview = false 
}: RefundFormContentProps) => {
  const { userLocation, isLoadingLocation } = useLocationHandling();
  const { currentStep, values, errors, handleChange, nextStep, prevStep, validateForm, setValues } = useRefundForm(userLocation);
  const { isSubmitting, handleSubmit } = useRefundSubmission();
  const { user } = useAuth();
  const { isLimitReached, requestsRemaining, limitPerMonth, isProTier } = useRequestLimitCheck();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async () => {
    if (validateForm()) {
      console.log("🚀 Form submission with values:", {
        messageFormat: values.messageFormat,
        merchant: values.merchant,
        issue: values.issue
      });
      
      // CRITICAL FIX: Pass the message format from form values explicitly
      const messageFormat = values.messageFormat || "email"; // fallback to email only if not set
      
      const submittedRequest = await handleSubmit(values, userLocation.country, messageFormat);
      setFormSubmitted(true);
      
      if (onSubmitSuccess && submittedRequest) {
        onSubmitSuccess(submittedRequest);
      }
      
      if (onCompletedCallback) {
        onCompletedCallback();
      }
    }
  };

  const handleUpgradeToPro = () => {
    navigate('/pricing');
  };

  // Only show limit reached alert for free tier users
  if (user && isLimitReached && !isPreview && !isProTier) {
    return (
      <div className="w-full max-w-2xl mx-auto px-4">
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Request Limit Reached</AlertTitle>
          <AlertDescription className="mb-4">
            You've used all {limitPerMonth} free refund requests for this month. 
            Please upgrade to our Pro plan for unlimited requests.
          </AlertDescription>
          <Button 
            onClick={handleUpgradeToPro}
            className="bg-gradient-to-r from-brand-primary to-brand-secondary hover:from-brand-primary/90 hover:to-brand-secondary/90 text-white font-medium"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade to Pro
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-4 sm:px-6">
      {user && !isPreview && (
        <div className="mb-6 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
            {isProTier ? (
              <>
                <Infinity className="w-4 h-4 mr-2" />
                Unlimited refund requests
              </>
            ) : (
              `${requestsRemaining} refund requests remaining this month`
            )}
          </div>
        </div>
      )}
      
      <div className="space-y-6">
        {currentStep === 1 && (
          <div className="animate-fade-in">
            <BasicInfoStep 
              values={values} 
              errors={errors} 
              onChange={handleChange} 
            />
          </div>
        )}
        
        {currentStep === 2 && (
          <Card className="animate-fade-in">
            <CardContent className="pt-6 px-4 sm:px-6">
              <AdditionalDetailsStep 
                values={values} 
                errors={errors} 
                onChange={handleChange}
                userLocation={userLocation}
                isLoadingLocation={isLoadingLocation}
              />
            </CardContent>
          </Card>
        )}
        
        <FormActions 
          currentStep={currentStep} 
          onNext={nextStep} 
          onPrev={prevStep} 
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          formSubmitted={formSubmitted}
          isLastStep={currentStep === 2}
        />
      </div>
    </div>
  );
};

export default RefundFormContent;
